import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Mic,
  Volume2,
  Wifi,
  Globe,
  CheckCircle,
  XCircle,
  Loader,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  Play,
  X,
  Monitor,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import "./PreMeetingCheck.scss";

/* ──────────────── check statuses ──────────────── */
const STATUS = { IDLE: "idle", CHECKING: "checking", PASS: "pass", FAIL: "fail" };

/* ──────────────── browser compatibility ──────────────── */
const checkBrowser = () => {
  const ua = navigator.userAgent;
  const isChrome = /Chrome/.test(ua) && !/Edg|OPR/.test(ua);
  const isEdge = /Edg/.test(ua);
  const isFirefox = /Firefox/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua);
  const supported = isChrome || isEdge || isFirefox;
  let name = "Unknown";
  if (isChrome) name = "Google Chrome";
  else if (isEdge) name = "Microsoft Edge";
  else if (isFirefox) name = "Mozilla Firefox";
  else if (isSafari) name = "Safari";
  return { supported, name };
};

/* ──────────────── internet speed test ──────────────── */
const measureSpeed = () =>
  new Promise((resolve) => {
    const imageUrl = "https://upload.wikimedia.org/wikipedia/commons/3/3a/Cat03.jpg";
    const fileSizeBytes = 741_000;
    const startTime = performance.now();
    fetch(imageUrl, { cache: "no-store", mode: "cors" })
      .then((r) => r.blob())
      .then((blob) => {
        const elapsed = (performance.now() - startTime) / 1000;
        const bytes = blob.size || fileSizeBytes;
        const mbps = ((bytes * 8) / elapsed / 1_000_000).toFixed(1);
        resolve(Number.parseFloat(mbps));
      })
      .catch(() => {
        if (navigator.connection?.downlink) {
          resolve(navigator.connection.downlink);
        }
        resolve(0);
      });
  });

/* ──────────────── mic level meter (0-10) ──────────────── */
const MIC_BARS = 10;

const MicLevel = ({ level }) => (
  <div className="pmc__mic-level">
    {Array.from({ length: MIC_BARS }, (_, i) => (
      <div
        key={i}
        className={`pmc__mic-level-bar ${i < level ? "pmc__mic-level-bar--active" : ""}`}
      />
    ))}
  </div>
);

/* ──────────────── status icon helper ──────────────── */
const StatusIcon = ({ status }) => {
  if (status === STATUS.PASS)
    return <CheckCircle size={16} color="#16a34a" className="pmc__status-icon" />;
  if (status === STATUS.FAIL)
    return <XCircle size={16} color="#dc2626" className="pmc__status-icon" />;
  if (status === STATUS.CHECKING)
    return <Loader size={16} className="pmc__status-icon pmc-spinner" />;
  return null;
};

/* ──────────────────────────────────────────────────
   PreMeetingCheck — modal-based system readiness check
   Props:
     show        — visibility flag
     role        — "instructor" | "student"
     batchName   — optional, for display
     onReady     — callback when user clicks "Join / Start"
     onCancel    — callback when user closes the modal
     confirmText — button label (default: "Join Class")
────────────────────────────────────────────────── */
const PreMeetingCheck = ({ show, role = "student", batchName, batchId, onReady, onCancel, confirmText = "Join Class" }) => {
  const [mic, setMic] = useState({ status: STATUS.IDLE, detail: "" });
  const [speaker, setSpeaker] = useState({ status: STATUS.IDLE, detail: "" });
  const [internet, setInternet] = useState({ status: STATUS.IDLE, detail: "", mbps: 0 });
  const [browser, setBrowser] = useState({ status: STATUS.IDLE, detail: "" });
  const [zoomHost, setZoomHost] = useState({ status: STATUS.IDLE, detail: "" });
  const [staleHosts, setStaleHosts] = useState([]);
  const [endingStale, setEndingStale] = useState(false);

  const [micLevel, setMicLevel] = useState(0);
  const [allDone, setAllDone] = useState(false);
  const [hasFailure, setHasFailure] = useState(false);
  const audioCtxRef = useRef(null);
  const micStreamRef = useRef(null);
  const analyserTimerRef = useRef(null);
  const analyserRef = useRef(null);

  // Device lists & selection
  const [microphones, setMicrophones] = useState([]);
  const [speakers, setSpeakers] = useState([]);
  const [selectedMic, setSelectedMic] = useState("");
  const [selectedSpeaker, setSelectedSpeaker] = useState("");
  const [micLiveActive, setMicLiveActive] = useState(false);

  // Enumerate devices
  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const mics = devices.filter((d) => d.kind === "audioinput");
      const spks = devices.filter((d) => d.kind === "audiooutput");
      setMicrophones(mics);
      setSpeakers(spks);
      if (!selectedMic && mics.length) setSelectedMic(mics[0].deviceId);
      if (!selectedSpeaker && spks.length) setSelectedSpeaker(spks[0].deviceId);
    } catch {
      // permissions not yet granted — will retry after mic check
    }
  }, [selectedMic, selectedSpeaker]);

  useEffect(() => {
    if (!show) return;
    enumerateDevices();
    navigator.mediaDevices.addEventListener?.("devicechange", enumerateDevices);
    return () => {
      navigator.mediaDevices.removeEventListener?.("devicechange", enumerateDevices);
    };
  }, [enumerateDevices, show]);

  // Cleanup on unmount or hide
  const cleanup = useCallback(() => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (analyserTimerRef.current) clearInterval(analyserTimerRef.current);
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    setMicLiveActive(false);
    setMicLevel(0);
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Reset & run checks when modal opens
  useEffect(() => {
    if (show) {
      runAllChecksRef.current();
    } else {
      cleanup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Close on Escape
  useEffect(() => {
    if (!show) return;
    const handleKey = (e) => { if (e.key === "Escape") onCancel?.(); };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [show, onCancel]);

  /* ──── Start live mic level monitor ──── */
  const startLiveMicMonitor = useCallback(async (deviceId) => {
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
    }
    if (analyserTimerRef.current) clearInterval(analyserTimerRef.current);
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});

    try {
      const constraints = deviceId
        ? { audio: { deviceId: { exact: deviceId } } }
        : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      micStreamRef.current = stream;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);

      analyserTimerRef.current = setInterval(() => {
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        const level = Math.min(MIC_BARS, Math.round((avg / 128) * MIC_BARS));
        setMicLevel(level);
      }, 100);
      setMicLiveActive(true);
    } catch {
      setMicLiveActive(false);
    }
  }, []);

  /* ──── 1. Browser check ──── */
  const runBrowserCheck = useCallback(() => {
    setBrowser({ status: STATUS.CHECKING, detail: "Checking…" });
    return new Promise((resolve) => {
      setTimeout(() => {
        const { supported, name } = checkBrowser();
        if (supported) {
          setBrowser({ status: STATUS.PASS, detail: `${name} — supported ✓` });
        } else {
          setBrowser({ status: STATUS.FAIL, detail: `${name} — use Chrome or Edge` });
        }
        resolve(supported);
      }, 400);
    });
  }, []);

  /* ──── 2. Microphone check ──── */
  const runMicCheck = useCallback(() => {
    setMic({ status: STATUS.CHECKING, detail: "Requesting access…" });
    setMicLevel(0);
    return new Promise((resolve) => {
      const constraints = selectedMic
        ? { audio: { deviceId: { exact: selectedMic } } }
        : { audio: true };
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          micStreamRef.current = stream;
          const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
          audioCtxRef.current = audioCtx;
          const source = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 256;
          source.connect(analyser);
          analyserRef.current = analyser;
          const data = new Uint8Array(analyser.frequencyBinCount);

          let peakDetected = false;
          let elapsed = 0;
          const onTick = () => {
            analyser.getByteFrequencyData(data);
            const avg = data.reduce((a, b) => a + b, 0) / data.length;
            const level = Math.min(MIC_BARS, Math.round((avg / 128) * MIC_BARS));
            setMicLevel(level);
            if (avg > 10) peakDetected = true;
            elapsed += 200;
            if (elapsed >= 2500) {
              clearInterval(analyserTimerRef.current);
              enumerateDevices();
              setMic(peakDetected
                ? { status: STATUS.PASS, detail: "Microphone working ✓" }
                : { status: STATUS.PASS, detail: "Connected (try speaking)" });
              startLiveMicMonitor(selectedMic);
              resolve(true);
            }
          };
          analyserTimerRef.current = setInterval(onTick, 200);
        })
        .catch((err) => {
          const denied = err.name === "NotAllowedError";
          setMic({
            status: STATUS.FAIL,
            detail: denied ? "Access denied — allow in browser" : "No microphone found",
          });
          resolve(false);
        });
    });
  }, [selectedMic, enumerateDevices, startLiveMicMonitor]);

  /* ──── 3. Speaker check ──── */
  const playTestTone = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (selectedSpeaker && ctx.setSinkId) {
        ctx.setSinkId(selectedSpeaker).catch(() => {});
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 440;
      gain.gain.value = 0.15;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      setTimeout(() => {
        osc.stop();
        ctx.close().catch(() => {});
      }, 800);
      return true;
    } catch {
      return false;
    }
  }, [selectedSpeaker]);

  const runSpeakerCheck = useCallback(() => {
    setSpeaker({ status: STATUS.CHECKING, detail: "Testing output…" });
    return new Promise((resolve) => {
      setTimeout(() => {
        const ok = playTestTone();
        if (ok) {
          setSpeaker({ status: STATUS.PASS, detail: "Speaker working ✓" });
        } else {
          setSpeaker({ status: STATUS.FAIL, detail: "Could not test speaker" });
        }
        resolve(ok);
      }, 100);
    });
  }, [playTestTone]);

  /* ──── 4. Internet speed check ──── */
  const runInternetCheck = useCallback(() => {
    setInternet({ status: STATUS.CHECKING, detail: "Measuring…", mbps: 0 });
    return measureSpeed().then((mbps) => {
      const good = mbps >= 5;
      setInternet({
        status: good ? STATUS.PASS : STATUS.FAIL,
        detail: good
          ? `${mbps} Mbps — good ✓`
          : `${mbps} Mbps — slow, switch network`,
        mbps,
      });
      return good;
    });
  }, []);

  /* ──── 5. Zoom host availability check (instructor + platformHosted only) ──── */
  const runZoomHostCheck = useCallback(async () => {
    if (role !== "instructor" || !batchId) {
      setZoomHost({ status: STATUS.PASS, detail: "Skipped" });
      return true;
    }
    setZoomHost({ status: STATUS.CHECKING, detail: "Checking classroom availability…" });
    setStaleHosts([]);
    try {
      const { data } = await apiClient.post(`/batches/${batchId}/pre-start-check`);
      if (data.available) {
        setZoomHost({ status: STATUS.PASS, detail: "Virtual classroom ready ✓" });
        return true;
      }
      if (data.staleHosts?.length > 0) {
        setStaleHosts(data.staleHosts);
        const names = data.staleHosts.map(h => h.batchName).join(", ");
        setZoomHost({
          status: STATUS.FAIL,
          detail: `Previous session not ended: ${names}`,
        });
        return false;
      }
      setZoomHost({ status: STATUS.FAIL, detail: data.message || "No classrooms available" });
      return false;
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not verify classroom";
      // If non-platformHosted or 400 (already live, etc.), treat as not blocking
      if (err?.response?.status === 400) {
        setZoomHost({ status: STATUS.FAIL, detail: msg });
        return false;
      }
      // If the check API fails entirely, don't block — let startMeeting handle it
      setZoomHost({ status: STATUS.PASS, detail: "Check skipped — will verify on start" });
      return true;
    }
  }, [batchId, role]);

  /* ──── Force end stale session ──── */
  const handleForceEndStale = useCallback(async (staleBatchId) => {
    if (!batchId || !staleBatchId) return;
    setEndingStale(true);
    try {
      await apiClient.post(`/batches/${batchId}/force-end-stale`, { staleBatchId });
      setStaleHosts([]);
      // Re-run zoom check after ending stale
      await runZoomHostCheck();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to end previous session";
      setZoomHost({ status: STATUS.FAIL, detail: msg });
    } finally {
      setEndingStale(false);
    }
  }, [batchId, runZoomHostCheck]);

  /* ──── Run all checks ──── */
  const runAllChecks = useCallback(async () => {
    setAllDone(false);
    setHasFailure(false);
    cleanup();

    const browserOk = await runBrowserCheck();
    const micOk = await runMicCheck();
    const speakerOk = await runSpeakerCheck();
    const internetOk = await runInternetCheck();
    const zoomOk = await runZoomHostCheck();

    setAllDone(true);
    setHasFailure(!browserOk || !micOk || !speakerOk || !internetOk || !zoomOk);
  }, [runBrowserCheck, runMicCheck, runSpeakerCheck, runInternetCheck, runZoomHostCheck, cleanup]);

  // Stable ref for useEffect
  const runAllChecksRef = useRef(runAllChecks);
  runAllChecksRef.current = runAllChecks;

  const handleMicChange = useCallback((deviceId) => {
    setSelectedMic(deviceId);
    if (micLiveActive) startLiveMicMonitor(deviceId);
  }, [micLiveActive, startLiveMicMonitor]);

  const handleSpeakerChange = useCallback((deviceId) => {
    setSelectedSpeaker(deviceId);
  }, []);

  const handleConfirm = () => {
    cleanup();
    onReady?.();
  };

  const handleClose = () => {
    cleanup();
    onCancel?.();
  };

  /* ──── Speed bar ──── */
  const speedPct = Math.min(100, (internet.mbps / 20) * 100);
  const roleLabel = role === "instructor" ? "Instructor" : "Student";

  if (!show) return null;

  return (
    <div className="pmc-overlay" onClick={handleClose}>
      <div
        className="pmc-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="System Check"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button className="pmc-dialog__close" onClick={handleClose} type="button" aria-label="Close">
          <X size={16} />
        </button>

        {/* Header */}
        <div className="pmc-dialog__header">
          <div className="pmc-dialog__header-icon">
            <Wifi size={22} />
          </div>
          <div>
            <h4 className="pmc-dialog__title">System Check</h4>
            <p className="pmc-dialog__subtitle">
              {batchName ? `${roleLabel} — ${batchName}` : "Checking your setup before joining"}
            </p>
          </div>
        </div>

        {/* Check items */}
        <div className="pmc-dialog__body">
          {/* Browser */}
          <div className={`pmc__item pmc__item--${browser.status}`}>
            <div className={`pmc__icon pmc__icon--${browser.status}`}>
              <Globe size={18} />
            </div>
            <div className="pmc__info">
              <p className="pmc__label">Browser</p>
              <p className="pmc__detail">{browser.detail || "Waiting…"}</p>
            </div>
            <StatusIcon status={browser.status} />
          </div>

          {/* Microphone */}
          <div className={`pmc__item pmc__item--${mic.status}`}>
            <div className={`pmc__icon pmc__icon--${mic.status}`}>
              <Mic size={18} />
            </div>
            <div className="pmc__info">
              <p className="pmc__label">Microphone</p>
              <p className="pmc__detail">{mic.detail || "Waiting…"}</p>
              {(mic.status === STATUS.CHECKING || micLiveActive) && <MicLevel level={micLevel} />}
              {microphones.length > 0 && (
                <div className="pmc__device-select">
                  <ChevronDown size={12} className="pmc__device-select-icon" />
                  <select value={selectedMic} onChange={(e) => handleMicChange(e.target.value)}>
                    {microphones.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || `Microphone ${microphones.indexOf(d) + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <StatusIcon status={mic.status} />
          </div>

          {/* Speaker */}
          <div className={`pmc__item pmc__item--${speaker.status}`}>
            <div className={`pmc__icon pmc__icon--${speaker.status}`}>
              <Volume2 size={18} />
            </div>
            <div className="pmc__info">
              <p className="pmc__label">Speaker</p>
              <p className="pmc__detail">{speaker.detail || "Waiting…"}</p>
              {speakers.length > 0 && (
                <div className="pmc__device-select">
                  <ChevronDown size={12} className="pmc__device-select-icon" />
                  <select value={selectedSpeaker} onChange={(e) => handleSpeakerChange(e.target.value)}>
                    {speakers.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>
                        {d.label || `Speaker ${speakers.indexOf(d) + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {speaker.status === STATUS.PASS && (
                <button type="button" className="pmc__test-btn" onClick={playTestTone}>
                  <Play size={11} /> Test Sound
                </button>
              )}
            </div>
            <StatusIcon status={speaker.status} />
          </div>

          {/* Internet Speed */}
          <div className={`pmc__item pmc__item--${internet.status}`}>
            <div className={`pmc__icon pmc__icon--${internet.status}`}>
              <Wifi size={18} />
            </div>
            <div className="pmc__info">
              <p className="pmc__label">Internet Speed</p>
              <p className={[
                "pmc__detail",
                internet.status === STATUS.PASS && "pmc__detail--good",
                internet.status === STATUS.FAIL && "pmc__detail--bad",
              ].filter(Boolean).join(" ")}>
                {internet.detail || "Waiting…"}
              </p>
              {(internet.status === STATUS.PASS || internet.status === STATUS.FAIL) && (
                <div className="pmc__speed-bar">
                  <div
                    className={`pmc__speed-bar-fill pmc__speed-bar-fill--${internet.mbps >= 5 ? "good" : "bad"}`}
                    style={{ width: `${speedPct}%` }}
                  />
                </div>
              )}
            </div>
            <StatusIcon status={internet.status} />
          </div>

          {/* Zoom Host Availability (instructor only) */}
          {role === "instructor" && batchId && (
            <div className={`pmc__item pmc__item--${zoomHost.status}`}>
              <div className={`pmc__icon pmc__icon--${zoomHost.status}`}>
                <Monitor size={18} />
              </div>
              <div className="pmc__info">
                <p className="pmc__label">Virtual Classroom</p>
                <p className="pmc__detail">{zoomHost.detail || "Waiting…"}</p>
                {staleHosts.length > 0 && (
                  <div className="pmc__stale-hosts">
                    {staleHosts.map((sh) => (
                      <div key={sh.batchId} className="pmc__stale-host-item">
                        <span className="pmc__stale-host-name">{sh.batchName}</span>
                        <button
                          type="button"
                          className="pmc__stale-end-btn"
                          disabled={endingStale}
                          onClick={() => handleForceEndStale(sh.batchId)}
                        >
                          {endingStale ? "Ending…" : "End & Proceed"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <StatusIcon status={zoomHost.status} />
            </div>
          )}
        </div>

        {/* Tip */}
        {hasFailure && allDone && (
          <div className="pmc__tip">
            <AlertTriangle size={14} />
            <span>Some checks failed. You can still join, but may experience issues.</span>
          </div>
        )}

        {/* Actions */}
        <div className="pmc-dialog__actions">
          <button className="pmc-dialog__btn pmc-dialog__btn--ghost" onClick={runAllChecks} disabled={!allDone} type="button">
            <RefreshCw size={13} /> Re-run
          </button>
          <button className="pmc-dialog__btn pmc-dialog__btn--cancel" onClick={handleClose} type="button">
            Cancel
          </button>
          <button className="pmc-dialog__btn pmc-dialog__btn--primary" onClick={handleConfirm} disabled={!allDone} type="button">
            {allDone ? confirmText : "Checking…"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreMeetingCheck;
